import type { DinComponent } from "./types.ts";

/**
 * Fabrikatoberoende generisk katalog. Täcker de vanligaste raderna i en svensk
 * elcentralspecifikation. Fabrikat-specifika poster (ABB, Schneider, Hager,
 * Siemens …) adderas senare via egna moduler.
 */

const mcbAmps = [6, 10, 13, 16, 20, 25, 32, 40, 50, 63] as const;
const mccbAmps = [100, 125, 160, 200, 250, 400, 630, 800] as const;

function mcb(
  A: number,
  poles: 1 | 3,
  char: "B" | "C" | "D",
): DinComponent {
  const polesStr = poles === 1 ? "1-pol" : "3-pol";
  return {
    key: `MCB-${poles}P-${char}${A}`,
    category: "MCB",
    label: `Dvärgbrytare ${polesStr} ${char}${A} A`,
    shortCodes: [`DB${A}`, `DB${poles}P${char}${A}`, poles === 1 ? `DB` : `DB3`],
    modules: poles === 1 ? 1 : 3,
    ampRating: A,
    poles,
    characteristic: char,
  };
}

function rcd(
  A: number,
  poles: 2 | 4,
  mA: number,
  type: "AC" | "A" | "B" | "F",
): DinComponent {
  return {
    key: `RCD-${poles}P-${A}-${mA}-${type}`,
    category: "RCD",
    label: `Jordfelsbrytare ${poles}-pol ${A} A, ${mA} mA typ ${type}`,
    shortCodes: [`JFB${A}`, `JFB${poles}P${mA}`, poles === 2 ? `JFB` : `JFB3`],
    modules: poles === 2 ? 2 : 4,
    ampRating: A,
    poles,
    sensitivity: mA,
    rcdType: type,
  };
}

function rcbo(A: number, char: "B" | "C", mA: number): DinComponent {
  return {
    key: `RCBO-1P-${char}${A}-${mA}`,
    category: "RCBO",
    label: `Jordfelsautomat 1-pol ${char}${A} A, ${mA} mA`,
    shortCodes: [`JFA${A}`, `RCBO${A}`, `JFA`],
    modules: 2,
    ampRating: A,
    poles: 1,
    characteristic: char,
    sensitivity: mA,
    rcdType: "A",
  };
}

function mccb(A: number, poles: 3 | 4): DinComponent {
  return {
    key: `MCCB-${poles}P-${A}`,
    category: "MCCB",
    label: `Effektbrytare ${poles}-pol ${A} A`,
    shortCodes: [`EB${A}`, `MCCB${A}`, `EB`],
    modules: poles === 3 ? 6 : 8,
    ampRating: A,
    poles,
    breakingCapacity: 25,
  };
}

function lbs(A: number, poles: 1 | 3 | 4): DinComponent {
  return {
    key: `LBS-${poles}P-${A}`,
    category: "LBS",
    label: `Lastbrytare ${poles}-pol ${A} A`,
    shortCodes: [`LB${A}`, `LB`],
    modules: poles,
    ampRating: A,
    poles,
  };
}

function fusedLbs(A: number, poles: 3): DinComponent {
  return {
    key: `SLF-${poles}P-${A}`,
    category: "FUSED-LBS",
    label: `Säkringslastbrytare ${poles}-pol ${A} A`,
    shortCodes: [`SLF${A}`, `SLF`],
    modules: poles * 2,
    ampRating: A,
    poles,
  };
}

/** 1-pol MCB (alla B-karakteristik — vanligast i bostäder). */
const mcbs1pB: DinComponent[] = mcbAmps.map((A) => mcb(A, 1, "B"));
/** 1-pol MCB C-karakteristik (motor/belastning med startström). */
const mcbs1pC: DinComponent[] = [10, 16, 20, 25, 32, 40, 63].map((A) => mcb(A, 1, "C"));
/** 3-pol MCB för kraftutrustning. */
const mcbs3pB: DinComponent[] = [10, 16, 20, 25, 32, 40, 63].map((A) => mcb(A, 3, "B"));
const mcbs3pC: DinComponent[] = [10, 16, 20, 25, 32, 40, 63].map((A) => mcb(A, 3, "C"));

/** 2-pol JFB 30 mA typ A — "standard-JFB" i bostäder. */
const rcds2p30A: DinComponent[] = [25, 40, 63].map((A) => rcd(A, 2, 30, "A"));
/** 4-pol JFB 30 mA typ A för trefasgrupper. */
const rcds4p30A: DinComponent[] = [25, 40, 63, 80, 100].map((A) => rcd(A, 4, 30, "A"));
/** 4-pol JFB 300 mA typ A — för ellsäkerhet på inkommande. */
const rcds4p300A: DinComponent[] = [63, 80, 100, 125].map((A) => rcd(A, 4, 300, "A"));
/** 4-pol JFB 30 mA typ B — laddstolpar / ellaxar med DC-läckström. */
const rcds4p30B: DinComponent[] = [40, 63].map((A) => rcd(A, 4, 30, "B"));

/** RCBO 30 mA typ A — ersätter JFB+MCB i moderna centraler. */
const rcbos: DinComponent[] = [10, 13, 16, 20, 25, 32].map((A) => rcbo(A, "B", 30));

/** MCCB kompaktbrytare 3- & 4-pol. */
const mccbs: DinComponent[] = mccbAmps.flatMap((A) => [mccb(A, 3), mccb(A, 4)]);

/** Lastbrytare — bostad och industri. */
const lastbrytare: DinComponent[] = [25, 40, 63, 80, 100, 125].flatMap((A) => [
  lbs(A, 1),
  lbs(A, 3),
]);

/** Säkringslastbrytare (SLF) — 3-pol för inkommande serviser. */
const sakringslastbrytare: DinComponent[] = [63, 100, 125, 160, 250].map((A) =>
  fusedLbs(A, 3),
);

/**
 * Överspänningsskydd (SPD / OSP). Type 1 = blixtströmsavledare (placeras
 * i inkommande central i byggnader med åskskyddsystem). Type 2 = skydd
 * mot inducerade överspänningar (vanligast i bostadscentraler). Type 1+2
 * = kombinationsskydd.
 */
const surgeProtection: DinComponent[] = [
  {
    key: "SPD-T1-4P",
    category: "SURGE",
    label: "Överspänningsskydd typ 1 (blixt), 4-pol",
    shortCodes: ["SPD1", "OSP1", "ÖSP1"],
    modules: 4,
    poles: 4,
  },
  {
    key: "SPD-T2-2P",
    category: "SURGE",
    label: "Överspänningsskydd typ 2, 2-pol",
    shortCodes: ["SPD2-2P", "OSP2-2P"],
    modules: 2,
    poles: 2,
  },
  {
    key: "SPD-T2-4P",
    category: "SURGE",
    label: "Överspänningsskydd typ 2, 4-pol",
    shortCodes: ["SPD", "SPD2", "OSP", "ÖSP", "OSP2"],
    modules: 4,
    poles: 4,
  },
  {
    key: "SPD-T12-4P",
    category: "SURGE",
    label: "Överspänningsskydd typ 1+2, 4-pol",
    shortCodes: ["SPD12", "OSP12", "SPD1+2"],
    modules: 4,
    poles: 4,
  },
];

/** PUS-plintar, sektioneringsplintar, allmänna plintar. */
const terminals: DinComponent[] = [
  {
    key: "PUS-PLINT",
    category: "TERMINAL",
    label: "PUS-plint (potentialutjämningsplint)",
    shortCodes: ["PUS"],
    modules: 2,
  },
  {
    key: "PLINT-SEKT",
    category: "TERMINAL",
    label: "Sektioneringsplint",
    shortCodes: ["PLINT", "SEKT"],
    modules: 1,
  },
  {
    key: "PLINT-N",
    category: "TERMINAL",
    label: "N-plint (nollskena)",
    shortCodes: ["N-PLINT", "NOLL"],
    modules: 1,
  },
];

/** Transformatorer / spänningsaggregat (T). */
const transformers: DinComponent[] = [
  {
    key: "T-230-24",
    category: "TRANSFORMER",
    label: "Transformator 230/24 V",
    shortCodes: ["T", "T24", "TRAFO"],
    modules: 4,
  },
  {
    key: "T-230-12",
    category: "TRANSFORMER",
    label: "Transformator 230/12 V",
    shortCodes: ["T12"],
    modules: 4,
  },
];

/** Energimätare, kontaktorer, hjälpreläer, timrar, impulsreläer. */
const misc: DinComponent[] = [
  {
    key: "METER-1F",
    category: "METER",
    label: "Energimätare 1-fas (kWh)",
    shortCodes: ["MÄT", "MÄT1", "KWH", "METER"],
    modules: 1,
    poles: 1,
  },
  {
    key: "METER-3F",
    category: "METER",
    label: "Energimätare 3-fas (kWh)",
    shortCodes: ["MÄT3", "KWH3", "METER3"],
    modules: 4,
    poles: 3,
  },
  {
    key: "METER-3F-CT",
    category: "METER",
    label: "Energimätare 3-fas med strömtrafo (kWhs)",
    shortCodes: ["KWHS", "MÄT3S"],
    modules: 4,
    poles: 3,
  },
  {
    key: "AUX-CONT-20A-2P",
    category: "AUX",
    label: "Kontaktor 20 A, 2-pol",
    shortCodes: ["K", "K20", "KONT"],
    modules: 2,
    ampRating: 20,
    poles: 2,
  },
  {
    key: "AUX-CONT-25A-4P",
    category: "AUX",
    label: "Kontaktor 25 A, 4-pol",
    shortCodes: ["K25", "KONT25"],
    modules: 3,
    ampRating: 25,
    poles: 4,
  },
  {
    key: "AUX-CONT-40A-4P",
    category: "AUX",
    label: "Kontaktor 40 A, 4-pol",
    shortCodes: ["K40", "KONT40"],
    modules: 3,
    ampRating: 40,
    poles: 4,
  },
  {
    key: "AUX-RELAY",
    category: "AUX",
    label: "Hjälprelä",
    shortCodes: ["HR", "RELÄ"],
    modules: 1,
  },
  {
    key: "AUX-IMPULSE",
    category: "AUX",
    label: "Impulsrelä",
    shortCodes: ["IR", "IMP"],
    modules: 1,
  },
  {
    key: "AUX-TIMER",
    category: "AUX",
    label: "Trappautomat / timer",
    shortCodes: ["UR", "TIMER", "TRAPP"],
    modules: 1,
  },
];

export const DIN_CATALOG: ReadonlyArray<DinComponent> = [
  ...mcbs1pB,
  ...mcbs1pC,
  ...mcbs3pB,
  ...mcbs3pC,
  ...rcbos,
  ...rcds2p30A,
  ...rcds4p30A,
  ...rcds4p300A,
  ...rcds4p30B,
  ...mccbs,
  ...lastbrytare,
  ...sakringslastbrytare,
  ...surgeProtection,
  ...terminals,
  ...transformers,
  ...misc,
];

export function getComponent(key: string): DinComponent | undefined {
  return DIN_CATALOG.find((c) => c.key === key);
}

export function findComponentsByShortCode(code: string): DinComponent[] {
  const upper = code.toUpperCase();
  return DIN_CATALOG.filter((c) =>
    c.shortCodes.some((sc) => sc.toUpperCase() === upper),
  );
}
